// InventoryTable.jsx - Enhanced Inventory Table with detailed information
import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Chip,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";

// This component renders an enhanced inventory table with visual indicators and an info button
const InventoryTable = ({
  items,
  page,
  rowsPerPage,
  handleOpenDetails,
  handleQrCode,
  handleOpenDialog,
  handleDelete,
  handleOpenUseDialog,
  getStockStatus,
  getStockStatusColor,
  calculateStockPercentage,
}) => {
  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Quantity</TableCell>
            <TableCell>Location</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((item) => {
              const stockStatus = getStockStatus(item);
              const statusColor = getStockStatusColor(stockStatus);
              const stockPercentage = calculateStockPercentage(item);

              return (
                <TableRow key={item._id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                        {item.name}
                      </Typography>
                      {item.isBookable && (
                        <Tooltip title="Bookable">
                          <Chip
                            label="B"
                            size="small"
                            color="primary"
                            sx={{ ml: 1, height: 16, fontSize: "0.6rem" }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.category}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontSize: "0.7rem",
                      }}
                    />
                  </TableCell>
                  <TableCell>{item.department}</TableCell>
                  <TableCell>
                    <Box sx={{ width: "100%", maxWidth: 120 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            bgcolor: statusColor,
                            mr: 1,
                          }}
                        />
                        <Typography variant="caption">
                          {stockStatus === "outOfStock"
                            ? "Out of Stock"
                            : stockStatus === "critical"
                            ? "Critical"
                            : stockStatus === "warning"
                            ? "Low Stock"
                            : "In Stock"}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stockPercentage}
                        sx={{
                          height: 4,
                          borderRadius: 1,
                          bgcolor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: statusColor,
                          },
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography>
                      {item.quantity} {item.unit}
                    </Typography>
                    {item.minThreshold && (
                      <Typography variant="caption" color="text.secondary">
                        Min: {item.minThreshold}{" "}
                        {item.minThresholdUnit || item.unit}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.location || "Not specified"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleOpenDetails(item)}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate QR Code">
                      <IconButton
                        size="small"
                        onClick={() => handleQrCode(item)}
                      >
                        <QrCodeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog("edit", item)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(item)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Use Item">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenUseDialog(item)}
                      >
                        <InventoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InventoryTable;
